import {
  createSerializer,
  type inferParserType,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

export const SORT_OPTIONS = [
  'date-desc',
  'date-asc',
  'price-desc',
  'price-asc',
  'updated-desc',
  'updated-asc',
  'alpha-desc',
  'alpha-asc',
] as const;

export type SortOption = (typeof SORT_OPTIONS)[number];

export const DEFAULT_SORT: SortOption = 'updated-desc';

export const sorters: Record<SortOption, { label: string }> = {
  'date-desc': {
    label:
      '\u05ea\u05d0\u05e8\u05d9\u05da \u05d4\u05d5\u05e1\u05e4\u05d4 (\u05de\u05d4\u05d7\u05d3\u05e9 \u05dc\u05d9\u05e9\u05df)',
  },
  'date-asc': {
    label:
      '\u05ea\u05d0\u05e8\u05d9\u05da \u05d4\u05d5\u05e1\u05e4\u05d4 (\u05de\u05d4\u05d9\u05e9\u05df \u05dc\u05d7\u05d3\u05e9)',
  },
  'price-desc': {
    label:
      '\u05de\u05d7\u05d9\u05e8 (\u05de\u05d4\u05d2\u05d1\u05d5\u05d4 \u05dc\u05e0\u05de\u05d5\u05da)',
  },
  'price-asc': {
    label:
      '\u05de\u05d7\u05d9\u05e8 (\u05de\u05d4\u05e0\u05de\u05d5\u05da \u05dc\u05d2\u05d1\u05d5\u05d4\u05d4)',
  },
  'updated-desc': {
    label:
      '\u05e2\u05d3\u05db\u05d5\u05df \u05de\u05d7\u05d9\u05e8 \u05d0\u05d7\u05e8\u05d5\u05df (\u05e1\u05d3\u05e8 \u05d9\u05d5\u05e8\u05d3)',
  },
  'updated-asc': {
    label:
      '\u05e2\u05d3\u05db\u05d5\u05df \u05de\u05d7\u05d9\u05e8 \u05d0\u05d7\u05e8\u05d5\u05df (\u05e1\u05d3\u05e8 \u05e2\u05d5\u05dc\u05d4)',
  },
  'alpha-desc': { label: '\u05d0\u05dc\u05e4\u05d1\u05d9\u05ea (\u05ea-\u05d0)' },
  'alpha-asc': { label: '\u05d0\u05dc\u05e4\u05d1\u05d9\u05ea (\u05d0-\u05ea)' },
};

/**
 * Single source of truth for all /items URL search params.
 * Used by: useItemsSearchParams(), createSerializer, itemsHref()
 */
export const itemsSearchParams = {
  page: parseAsInteger.withDefault(1),
  sort: parseAsStringLiteral(SORT_OPTIONS).withDefault(DEFAULT_SORT),
  query: parseAsString,
  filters: parseAsArrayOf(parseAsInteger, ',').withDefault([]),
  brands: parseAsArrayOf(parseAsInteger, ',').withDefault([]),
  brandName: parseAsString,
  minPrice: parseAsInteger,
  maxPrice: parseAsInteger,
  lowestPricesOnly: parseAsBoolean.withDefault(false),
  includeOutOfStock: parseAsBoolean.withDefault(false),
  backInStockOnly: parseAsBoolean.withDefault(false),
  afterDropOnly: parseAsBoolean.withDefault(false),
};

export function useItemsSearchParams() {
  return useQueryStates(itemsSearchParams, {
    history: 'push',
    clearOnDefault: true,
  });
}

export const serializeItemsSearch = createSerializer(itemsSearchParams);

export function itemsHref(params: Partial<ItemsSearchParams>): string {
  return `/items${serializeItemsSearch(params)}`;
}

export type ItemsSearchParams = inferParserType<typeof itemsSearchParams>;

/**
 * Parse raw URL search params into the same typed format as useItemsSearchParams().
 * Used in the route loader to ensure cache key alignment with the client hook.
 */
export function parseRawSearchParams(raw: Record<string, unknown>): ItemsSearchParams {
  function parse<T>(value: unknown, parser: { parse: (v: string) => T | null }): T | null {
    if (value == null) return null;
    const str = String(value);
    return str !== '' ? parser.parse(str) : null;
  }

  return {
    page: parse(raw.page, parseAsInteger) ?? 1,
    sort: parse(raw.sort, parseAsStringLiteral(SORT_OPTIONS)) ?? DEFAULT_SORT,
    query: parse(raw.query, parseAsString) ?? null,
    filters: parse(raw.filters, parseAsArrayOf(parseAsInteger, ',')) ?? [],
    brands: parse(raw.brands, parseAsArrayOf(parseAsInteger, ',')) ?? [],
    brandName: parse(raw.brandName, parseAsString) ?? null,
    minPrice: parse(raw.minPrice, parseAsInteger) ?? null,
    maxPrice: parse(raw.maxPrice, parseAsInteger) ?? null,
    lowestPricesOnly: parse(raw.lowestPricesOnly, parseAsBoolean) ?? false,
    includeOutOfStock: parse(raw.includeOutOfStock, parseAsBoolean) ?? false,
    backInStockOnly: parse(raw.backInStockOnly, parseAsBoolean) ?? false,
    afterDropOnly: parse(raw.afterDropOnly, parseAsBoolean) ?? false,
  };
}
